import { Component, ElementRef, HostListener, Input, TemplateRef } from '@angular/core'
import { Router } from '@angular/router'
import { INavigationItem } from '@engineering11/ui-lib/e11-nav-left'
import { IEmployerNavigationItem } from '@employer/app/services/navigation.service'
import { navTourAnchor } from '@employer/app/services/nav-tour-anchor'

/**
 * Icon-only rendering of the left nav, used when the rail is collapsed (desktop
 * preference or tablet-forced). Replaces the ui-lib `iconOnly` mode, where group
 * icons were a dead end (children were force-closed and unreachable).
 *
 * Behavior (R6 of the approved contract):
 *   - A LEAF icon navigates directly (routerLink) and shows the active highlight
 *     when its route — or any route it owns — is current.
 *   - A GROUP icon opens a flyout panel beside the rail listing the group's items,
 *     rendered with the same `app-nav-item` rules (sub-groups expand in place).
 *     Choosing a leaf inside the flyout navigates and closes it; Escape or an
 *     outside click also closes it. The rail itself stays collapsed.
 */
@Component({
  selector: 'app-nav-icon-rail',
  template: `
    <div class="e11-relative e11-h-full e11-flex e11-flex-col">
      <div class="e11-flex-1 e11-min-h-0 e11-overflow-y-auto e11-flex e11-flex-col e11-items-center e11-gap-1 e11-text-skin-light">
        <ng-container *ngFor="let item of items">
          <ng-container *ngIf="!item.isVisible || (item.isVisible | async)">
            <!-- Custom-slot item (Messages): app template, icon-only variant (R8) -->
            <ng-container *ngIf="item.customTemplateKey && customTemplates[item.customTemplateKey]">
              <ng-container *ngTemplateOutlet="customTemplates[item.customTemplateKey]"></ng-container>
            </ng-container>

            <ng-container *ngIf="!item.customTemplateKey">
              <!-- Group icon → flyout -->
              <button
                *ngIf="item.children?.length"
                type="button"
                [attr.data-tour-id]="tourAnchor(item)"
                class="e11-relative e11-w-11 e11-h-11 e11-rounded-lg e11-flex e11-items-center e11-justify-center e11-cursor-pointer e11-bg-transparent e11-border-0 hover:e11-text-white hover:e11-bg-skin-white/10 focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-white/50"
                [ngClass]="isActiveInside(item) ? 'e11-bg-skin-white/10 e11-text-skin-white' : 'e11-text-skin-light'"
                [title]="item.name"
                [attr.aria-label]="item.name"
                aria-haspopup="menu"
                [attr.aria-expanded]="flyoutItem === item"
                (click)="toggleFlyout(item, $event)"
              >
                <span class="material-symbols-outlined md-24">{{ item.icon }}</span>
                <span class="rail-group-dot" aria-hidden="true"></span>
              </button>

              <!-- Leaf icon → navigate -->
              <a
                *ngIf="!item.children?.length"
                [attr.data-tour-id]="tourAnchor(item)"
                class="e11-w-11 e11-h-11 e11-rounded-lg e11-flex e11-items-center e11-justify-center e11-cursor-pointer e11-text-skin-light hover:e11-text-white hover:e11-bg-skin-white/10 focus-visible:e11-outline focus-visible:e11-outline-2 focus-visible:e11-outline-skin-white/50"
                [routerLink]="item.route"
                [queryParams]="item.navigationExtras?.queryParams"
                routerLinkActive
                #rla="routerLinkActive"
                [routerLinkActiveOptions]="{ exact: true }"
                [ngClass]="(item.isActive ? isActiveNow(item) : rla.isActive) ? 'e11-bg-skin-white/10 e11-text-skin-white' : ''"
                [title]="item.name"
                [attr.aria-label]="item.name"
                (click)="closeFlyout()"
              >
                <span class="material-symbols-outlined md-24">{{ item.icon }}</span>
              </a>
            </ng-container>
          </ng-container>
        </ng-container>
      </div>

      <!-- Flyout: the collapsed-rail path into a group's children (R6) -->
      <div
        *ngIf="flyoutItem"
        class="nav-flyout e11-absolute e11-w-60 e11-p-2 e11-rounded-lg e11-bg-skin-app-bg-dark e11-border e11-border-skin-grey/40 e11-shadow-xl e11-text-skin-light e11-z-50"
        [style.top.px]="flyoutTop"
        role="menu"
        [attr.aria-label]="flyoutItem.name"
      >
        <div
          class="e11-flex e11-items-center e11-gap-2 e11-px-2 e11-pb-2 e11-mb-1 e11-border-b e11-border-skin-grey/30 e11-text-skin-white e11-font-primary-demibold e11-text-sm"
        >
          <span *ngIf="flyoutItem.icon" class="material-symbols-outlined md-18">{{ flyoutItem.icon }}</span>
          {{ flyoutItem.name }}
        </div>
        <app-nav-item
          *ngFor="let child of flyoutItem.children"
          [item]="child"
          [level]="1"
          [openState]="flyoutOpenState"
          [customTemplates]="customTemplates"
          (leafNavigated)="closeFlyout()"
        ></app-nav-item>
      </div>
    </div>
  `,
  styles: [
    `
      .nav-flyout {
        left: calc(100% + 10px);
      }
      /* Corner tick marking "this icon opens a group" */
      .rail-group-dot {
        position: absolute;
        right: 5px;
        bottom: 5px;
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-bottom: 5px solid currentColor;
        opacity: 0.65;
        transform: rotate(-45deg);
      }
    `,
  ],
})
export class NavIconRailComponent {
  @Input() items: IEmployerNavigationItem[] | null = []
  @Input() customTemplates: Record<string, TemplateRef<unknown>> = {}

  tourAnchor = navTourAnchor

  flyoutItem?: IEmployerNavigationItem
  flyoutTop = 8
  // The flyout hosts nav-items too, so it needs its own (throwaway) expand-state map.
  flyoutOpenState = new Map<INavigationItem, boolean>()

  constructor(private router: Router, private host: ElementRef<HTMLElement>) {}

  /**
   * Collapsed-rail counterpart of `nav-item`'s active override (CNCT-6779): rows that
   * own more routes than their own — Company owns the whole settings context — decide
   * their own highlight. Read synchronously off `router.url`; the rail re-renders on
   * navigation because `isActiveInside` is evaluated the same way.
   */
  isActiveNow(item: IEmployerNavigationItem): boolean {
    return item.isActive ? item.isActive(this.router.url) : false
  }

  toggleFlyout(item: INavigationItem, event: MouseEvent): void {
    if (this.flyoutItem === item) {
      this.closeFlyout()
      return
    }
    this.flyoutItem = item
    this.flyoutOpenState = new Map()
    // Anchor the flyout to the clicked icon, clamped so it never overflows the rail bottom.
    const button = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const rail = this.host.nativeElement.getBoundingClientRect()
    const maxTop = Math.max(8, rail.height - 320)
    this.flyoutTop = Math.min(Math.max(8, button.top - rail.top), maxTop)
  }

  closeFlyout(): void {
    this.flyoutItem = undefined
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeFlyout()
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.flyoutItem && !this.host.nativeElement.contains(event.target as Node)) this.closeFlyout()
  }

  /** Active when the current URL is the item's own route or owned by its subtree. */
  isActiveInside(item: INavigationItem, url: string = this.router.url): boolean {
    if (typeof item.isOpen === 'function' && item.isOpen(url)) return true
    if (typeof item.route === 'string' && url.split('?')[0].replace(/\/$/, '') === item.route.replace(/\/$/, '')) return true
    return (item.children ?? []).some(child => this.isActiveInside(child, url))
  }
}
